import { ServerConfig } from "@/config/config";
import { Axios } from "@/utils/axios";

export type IntegrationType = "manual" | "exact" | "clearfacts" | "winbooks" | "yuki";

export interface IAccountantDossier {
	DossierID: number;
	DossierName: string;
	VATNumber: string;
	CompanyID: number;
	CompanyName: string;
	IntegrationType: IntegrationType;
	Config: string;
	Enabled: boolean;
	IsDemo: boolean;
	Tier: number;
}

export type AccountantInviteStatus = "pending" | "registered";

export interface IAccountantInvite {
	ID: number;
	Email: string;
	Status: AccountantInviteStatus;
	DossierID: number | null;
	DossierName: string;
	CompanyID: number | null;
	CompanyName: string;
	IsDemo: boolean;
	Tier: number;
	DateSent: string;
	DateRegistered: string | null;
}

export interface IExactDossierConfig {
	division_id: number;
	division_name: string;
	journal?: string;
	default_vat_code?: string;
	default_item_id?: string;
	default_gl_account_id?: string;
	leeggoed_gl_account_id?: string;
	purchase_journal?: string;
	default_purchase_vat_code?: string;
	default_purchase_gl_account_id?: string;
	auto_sync_sales?: boolean;
	auto_sync_purchases?: boolean;
	auto_process_sales?: boolean;
	attach_pdf?: boolean;
	sales_export_mode?: "invoice" | "entry";
	vat_code_overrides?: IExactVatCodeOverride[];
	purchase_vat_code_overrides?: IExactVatCodeOverride[];
}

export interface IExactVatCodeOverride {
	regime: number;
	rate: number;
	code: string;
}

export interface IWinbooksVatCodeEntry {
	regime: number;
	rate: number;
	code: string;
	ledger_account?: string;
}

export interface IWinbooksDossierConfig {
	sales_journal_invoice?: string;
	sales_journal_invoice_type?: string;
	sales_journal_credit?: string;
	sales_journal_credit_type?: string;
	central_account?: string;
	vat_account?: string;
	default_ledger_account?: string;
	leeggoed_account?: string;
	account_length?: number;
	decimals?: number;
	quarter_periods?: boolean;
	book_year_code?: string;
	customer_number_field?: number;
	vat_codes?: IWinbooksVatCodeEntry[];
}

export interface IYukiDossierConfig {
	api_key?: string;
	administration_id?: string;
	domain?: "be" | "nl";
	process?: boolean;
	attach_pdf?: boolean;
	default_gl_account?: string;
}

export interface IClearfactsDossierConfig {
	auth_method?: "oauth" | "token";
	api_key?: string;
	vat_number?: string;
	administration_name?: string;
	sales_format?: string;
	purchase_format?: string;
	auto_sync_sales?: boolean;
	auto_sync_purchases?: boolean;
}

export interface IExactSyncRun {
	ID: number;
	DossierID: number;
	RunAt: string;
	Trigger: string;
	DocumentType: string;
	Created: number;
	Skipped: number;
	Failed: number;
	Errors: string;
}

export interface IExactSyncRunError {
	FriendlyID: string;
	Error: string;
}

export type NotificationFrequency = "daily" | "weekly" | "monthly";

export interface INotificationSettings {
	Enabled: boolean;
	Frequency: NotificationFrequency;
	NotifyOnError: boolean;
	Email: string;
	DossierIDs: number[];
	NextSendAt: string | null;
	LastSentAt: string | null;
}

export interface INotificationTestResult {
	Email: string;
	Dossiers: number;
	Period: string;
	IsSample?: boolean;
}

export class AccountantServiceClass {
	private readonly baseUrl:string = `${ServerConfig.auth}/auth/accountant`;

	async getDossiers():Promise<IAccountantDossier[]>{
		let result = await Axios.get(`${this.baseUrl}/dossiers`);
		return result.data || [];
	}

	async setIntegration(dossierId:number, integrationType:IntegrationType, config:object | null, enabled:boolean):Promise<IAccountantDossier>{
		let result = await Axios.put(`${this.baseUrl}/dossiers/${dossierId}/integration`, {
			IntegrationType: integrationType,
			Config: config,
			Enabled: enabled
		});
		return result.data;
	}

	async getExactSyncRuns(dossierId:number):Promise<IExactSyncRun[]>{
		let result = await Axios.get(`${this.baseUrl}/dossiers/${dossierId}/exact-sync-runs`);
		return result.data || [];
	}

	async getNotificationSettings():Promise<INotificationSettings>{
		let result = await Axios.get(`${this.baseUrl}/notification-settings`);
		return result.data;
	}

	async saveNotificationSettings(settings:INotificationSettings):Promise<INotificationSettings>{
		let result = await Axios.put(`${this.baseUrl}/notification-settings`, settings);
		return result.data;
	}

	async sendNotificationTest():Promise<INotificationTestResult>{
		let result = await Axios.post(`${this.baseUrl}/notification-settings/test`);
		return result.data;
	}

	async sendNotificationErrorTest():Promise<INotificationTestResult>{
		let result = await Axios.post(`${this.baseUrl}/notification-settings/test-error`);
		return result.data;
	}

	async getInvites():Promise<IAccountantInvite[]>{
		let result = await Axios.get(`${this.baseUrl}/invites`);
		return result.data || [];
	}

	async sendInvite(email:string):Promise<void>{
		await Axios.post(`${this.baseUrl}/invites`, {email});
	}

	async cancelInvite(inviteId:number):Promise<void>{
		await Axios.delete(`${this.baseUrl}/invites/${inviteId}`);
	}
}

export const AccountantService = new AccountantServiceClass();
